import React, { useState } from "react";
import { toast } from "react-toastify";
import client from "../../../api/client";

const SPONSORSHIP_TYPES = ["Company", "Foundation", "Individual", "Not sure yet"];
const SEAT_RANGES = ["1-5", "6-20", "21-50", "50+", "Not sure yet"];

const CONTACT_PHONE = "+1 (202) 933-9591";
const CONTACT_PHONE_HREF = "tel:+12029339591";
const CONTACT_EMAIL = "info@e1xacademy.net";

const EMPTY = {
  full_name: "",
  organization: "",
  role: "",
  email: "",
  phone: "",
  sponsorship_option: "",
  seats: "",
  goals: "",
};

const inputClass =
  "mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-[#0b0f19] outline-none focus:border-[#d15424] focus:ring-2 focus:ring-[#d15424]/20";

function Field({ label, optional, error, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[#0b0f19]">
        {label}
        {optional && <span className="font-normal text-gray-500"> (optional)</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
    </label>
  );
}

export default function SponsorForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Backend stores first/last name separately.
    const [first_name, ...rest] = form.full_name.trim().split(/\s+/);
    const { full_name, ...fields } = form;

    try {
      await client.post("/sponsor", { ...fields, first_name, last_name: rest.join(" ") });
      setSent(true);
      setForm(EMPTY);
    } catch (error) {
      const fieldErrors = error.response?.data?.errors;
      if (fieldErrors) {
        const flat = Object.fromEntries(
          Object.entries(fieldErrors).map(([k, v]) => [k === "first_name" ? "full_name" : k, v[0]])
        );
        setErrors(flat);
        toast.error("Please check the highlighted fields.");
      } else {
        toast.error("Something went wrong sending your enquiry. Please try again.");
      }
    }
    setLoading(false);
  };

  return (
    <section className="bg-[#f7f7f7] px-4 md:px-[7%] pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl md:text-5xl font-bold text-[#0b0f19] text-center">
          Become a Sponsor
        </h1>

        <div className="mt-10 rounded-2xl bg-white p-6 md:p-10 shadow-sm border border-gray-200">
          {sent ? (
            <div className="text-center py-10">
              <h2 className="text-2xl font-bold text-[#0b0f19]">Thank you, your enquiry has been sent.</h2>
              <p className="mt-3 text-gray-600">We will respond within 48 hours.</p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 text-sm font-bold text-[#d15424] hover:underline"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <Field label="Full name" error={errors.full_name}>
                  <input required className={inputClass} value={form.full_name} onChange={set("full_name")} autoComplete="name" />
                </Field>
              </div>
              <Field label="Email" error={errors.email}>
                <input required type="email" className={inputClass} value={form.email} onChange={set("email")} autoComplete="email" />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input required type="tel" className={inputClass} value={form.phone} onChange={set("phone")} autoComplete="tel" />
              </Field>
              <Field label="Organisation" optional error={errors.organization}>
                <input className={inputClass} value={form.organization} onChange={set("organization")} autoComplete="organization" />
              </Field>
              <Field label="Role" error={errors.role}>
                <input required className={inputClass} value={form.role} onChange={set("role")} autoComplete="organization-title" />
              </Field>
              <Field label="Sponsorship type" error={errors.sponsorship_option}>
                <select required className={inputClass} value={form.sponsorship_option} onChange={set("sponsorship_option")}>
                  <option value="" disabled>Select one</option>
                  {SPONSORSHIP_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Approximate seats" error={errors.seats}>
                <select required className={inputClass} value={form.seats} onChange={set("seats")}>
                  <option value="" disabled>Select a range</option>
                  {SEAT_RANGES.map((r) => (
                    <option key={r} value={r}>{r.replace("-", "–")}</option>
                  ))}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="What do you want your sponsorship to achieve?" error={errors.goals}>
                  <textarea required rows={6} maxLength={5000} className={inputClass} value={form.goals} onChange={set("goals")} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#d15424] py-4 font-bold text-white transition hover:bg-[#b8461d] disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send enquiry"}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-gray-600">
          We will respond within 48 hours. Prefer to talk? Call{" "}
          <a href={CONTACT_PHONE_HREF} className="font-semibold text-[#d15424] hover:underline">{CONTACT_PHONE}</a>{" "}
          or email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-[#d15424] hover:underline">{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </section>
  );
}
